import apiClient from '@/lib/api-client';
import { UpdateForm } from '@/model/form/UpdateForm';
import { ApiError } from '@/model/ApiError';
import { PasswordFormData, ProfileFormData, User } from '@/model/Profile';
import {
    createSignupRequestFromFormData,
    SignupFormData,
    SignupRequest,
} from '@/model/Signup';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import Pageable from '@/model/Pageable';
import { PackageHistory } from '@/model/Project';
import { EventResponse } from '@/model/Event';
import { createDateFromUTCString } from '@/lib/date-util';
import { ManagedDocumentResponse } from '@/model/Document';
import FileUsageType from '@/model/enums/FileUsageType';
import downloadBlobFromEndpoint from '@/lib/blob-download-util';
import { DEFAULT_EXCEL_TEMPLATE_FILE_NAME } from '@/lib/excel-util';
import PAGE_SIZE from '@/lib/pagination';

const SYSTEM_DOCUMENT_USAGE_TYPES: FileUsageType[] = [
    FileUsageType.GTC,
    FileUsageType.GDPR,
    FileUsageType.EXCEL_TEMPLATE,
];

export type UserSortDirection = 'asc' | 'desc';

export type GetAllUserRequest = {
    page: number;
    sortField?: string;
    sortDirection?: UserSortDirection;
};

function createEmptyManagedDocument(
    usageType: FileUsageType,
): ManagedDocumentResponse {
    return {
        usageType,
        fileId: null,
        fileName: null,
        contentType: null,
        sizeInBytes: null,
        uploadedAt: null,
        uploadedById: null,
        uploadedByName: null,
    };
}

function normalizeManagedDocument(
    document: ManagedDocumentResponse,
): ManagedDocumentResponse {
    const normalizedDocument = {
        ...document,
    };

    if (
        normalizedDocument.uploadedAt &&
        !(normalizedDocument.uploadedAt instanceof Date)
    ) {
        normalizedDocument.uploadedAt = createDateFromUTCString(
            normalizedDocument.uploadedAt.toString(),
        );
    }

    return normalizedDocument;
}

function mergeManagedDocuments(
    documents: ManagedDocumentResponse[],
): ManagedDocumentResponse[] {
    const mergedDocuments = SYSTEM_DOCUMENT_USAGE_TYPES.map(
        createEmptyManagedDocument,
    );

    documents.forEach((document) => {
        const normalizedDocument = normalizeManagedDocument(document);
        const existingIndex = mergedDocuments.findIndex(
            (managedDocument) =>
                managedDocument.usageType === normalizedDocument.usageType,
        );

        if (existingIndex === -1) {
            mergedDocuments.push(normalizedDocument);
            return;
        }

        mergedDocuments.splice(existingIndex, 1, normalizedDocument);
    });

    return mergedDocuments;
}

@Module({ namespaced: true, preserveState: false })
export default class AdminStorage extends VuexModule {
    error: ApiError | null = null;

    passwordError: ApiError | null = null;

    users: Pageable<User> | null = null;

    events: Pageable<EventResponse> | null = null;

    documents: ManagedDocumentResponse[] = mergeManagedDocuments([]);

    loading = false;

    historyLoading = false;

    documentsLoading = false;

    @Mutation
    setLoading(newLoading: boolean): void {
        this.loading = newLoading;
    }

    @Mutation
    setActiveForUser(request: { userId: number; newActive: boolean }): void {
        const users = this.users?.items;

        if (!users) {
            return;
        }

        users.forEach((user) => {
            if (user.id === request.userId) {
                // eslint-disable-next-line no-param-reassign
                user.active = request.newActive;
            }
        });
    }

    @Mutation
    setHistoryLoading(newLoading: boolean): void {
        this.historyLoading = newLoading;
    }

    @Mutation
    setDocumentsLoading(newLoading: boolean): void {
        this.documentsLoading = newLoading;
    }

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setPasswordError(error: ApiError | null): void {
        this.passwordError = error;
    }

    @Mutation
    setPackageHistory(response: {
        id: number;
        newPackageHistory: PackageHistory[];
    }): void {
        const users = this.users?.items;

        if (!users) {
            return;
        }

        users.forEach((user) => {
            if (user.projectId === response.id) {
                const tmpPackageHistory = response.newPackageHistory;
                tmpPackageHistory.forEach((tmpPackage) => {
                    // eslint-disable-next-line no-param-reassign
                    tmpPackage.startedAt += 'Z';
                    // eslint-disable-next-line no-param-reassign
                    if (tmpPackage.endedAt) {
                        tmpPackage.endedAt += 'Z';
                    }
                });
                // eslint-disable-next-line no-param-reassign
                user.packageHistory = tmpPackageHistory;
            }
        });
    }

    @Mutation
    setUsers(newUsers: Pageable<User> | null): void {
        if (newUsers) {
            newUsers.items.forEach((user) => {
                user.fullName = `${user.firstName} ${user.lastName}`;
            });
        }
        this.users = newUsers;
    }

    @Mutation
    setUser(updatedUser: User): void {
        const users = this.users?.items;

        if (!users) {
            return;
        }

        const existingIndex = users.findIndex(
            (user) => user.id === updatedUser.id,
        );

        if (existingIndex === -1) {
            return;
        }

        const existingUser = users[existingIndex];
        const normalizedUser = {
            ...existingUser,
            ...updatedUser,
            fullName: `${updatedUser.firstName} ${updatedUser.lastName}`,
            packageHistory:
                updatedUser.packageHistory ?? existingUser.packageHistory,
        };

        users.splice(existingIndex, 1, normalizedUser);
    }

    @Mutation
    setEvents(events: Pageable<EventResponse> | null): void {
        if (events) {
            events.items.forEach((event) => {
                if (event.selectedStartDate) {
                    event.selectedStartDate = new Date(event.selectedStartDate);
                }

                if (event.startDate) {
                    event.startDate = new Date(event.startDate);
                }

                if (event.createdAt) {
                    event.createdAt = createDateFromUTCString(
                        event.createdAt.toString(),
                    );
                }

                if (event.deletedAt) {
                    event.deletedAt = createDateFromUTCString(
                        event.deletedAt.toString(),
                    );
                }
            });
        }
        this.events = events;
    }

    @Mutation
    setDocuments(documents: ManagedDocumentResponse[]): void {
        this.documents = mergeManagedDocuments(documents);
    }

    @Mutation
    setDocument(document: ManagedDocumentResponse): void {
        this.documents = mergeManagedDocuments([
            ...this.documents.filter(
                (managedDocument) =>
                    managedDocument.usageType !== document.usageType,
            ),
            document,
        ]);
    }

    @Mutation
    deleteUser(id: number): void {
        if (this.users?.items) {
            this.users.items = this.users.items.filter(
                (user) => user.id !== id,
            );
        }
    }

    @Action
    getAllUser({
        page,
        sortField,
        sortDirection,
    }: GetAllUserRequest): Promise<void> {
        const params: { page: number; size: number; sort?: string } = {
            page,
            size: PAGE_SIZE,
        };

        if (sortField && sortDirection) {
            params.sort = `${sortField},${sortDirection}`;
        }

        this.context.commit('setLoading', true);
        return apiClient
            .get<number, Pageable<User>>('/users', {
                params,
            })
            .then((data: Pageable<User>) => {
                this.context.commit('setUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            })
            .finally(() => {
                this.context.commit('setLoading', false);
            });
    }

    @Action
    fetchEvents(page: number): Promise<void> {
        return apiClient
            .get<void, Pageable<EventResponse>>('/events/admin', {
                params: {
                    page,
                    size: PAGE_SIZE,
                },
            })
            .then((data: Pageable<EventResponse>) => {
                this.context.commit('setEvents', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchDocuments(): Promise<void> {
        this.context.commit('setDocumentsLoading', true);

        return apiClient
            .get<void, ManagedDocumentResponse[]>('/documents')
            .then((data: ManagedDocumentResponse[]) => {
                this.context.commit('setDocuments', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                throw error;
            })
            .finally(() => {
                this.context.commit('setDocumentsLoading', false);
            });
    }

    @Action
    uploadDocument(request: {
        usageType: FileUsageType;
        file: File;
    }): Promise<ManagedDocumentResponse> {
        const formData = new FormData();
        formData.append('file', request.file);

        return apiClient
            .post<FormData, ManagedDocumentResponse>(
                `/documents/${request.usageType}`,
                formData,
            )
            .then((data: ManagedDocumentResponse) => {
                this.context.commit('setDocument', data);
                this.context.commit('setError', null);
                return data;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                throw error;
            });
    }

    @Action
    downloadExcelTemplate(): Promise<void> {
        const storedFileName = this.documents.find(
            (document) => document.usageType === FileUsageType.EXCEL_TEMPLATE,
        )?.fileName;

        return downloadBlobFromEndpoint(
            '/documents/EXCEL_TEMPLATE/download',
            storedFileName ?? DEFAULT_EXCEL_TEMPLATE_FILE_NAME,
        )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                throw error;
            });
    }

    @Action
    signup(formData: SignupFormData): Promise<void> {
        return apiClient
            .post<SignupRequest, void>(
                '/auth/signup',
                createSignupRequestFromFormData(formData),
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deactivateUserById(id: number): Promise<void> {
        return apiClient
            .delete(`/users/${id}/deactivate`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('setActiveForUser', {
                    userId: id,
                    newActive: false,
                });
                const button = document.getElementById(`btn${id}`);
                if (button) {
                    button.blur();
                }
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    reactivateUserById(id: number): Promise<void> {
        return apiClient
            .put(`/users/${id}/reactivate`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('setActiveForUser', {
                    userId: id,
                    newActive: true,
                });
                const button = document.getElementById(`btn${id}`);
                if (button) {
                    button.blur();
                }
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deleteUserById(id: number): Promise<void> {
        return apiClient
            .delete(`/users/${id}`)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateAdminProfile(request: UpdateForm<ProfileFormData>): Promise<User> {
        return apiClient
            .put<ProfileFormData, User>(
                `/users/${request.id}/admin`,
                request.formData,
            )
            .then((updatedUser: User) => {
                this.context.commit('setError', null);
                this.context.commit('setUser', updatedUser);

                const currentUser =
                    this.context.rootGetters['authenticationStorage/getUser'];

                if (currentUser?.id === request.id) {
                    this.context.commit(
                        'authenticationStorage/setUserFirstNameLastNamePhone',
                        request.formData,
                        { root: true },
                    );
                }

                return updatedUser;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                throw error;
            });
    }

    @Action
    updateUserChatbotEnabled(request: {
        user: User;
        chatbotEnabled: boolean;
    }): Promise<User> {
        const formData: ProfileFormData = {
            firstName: request.user.firstName,
            lastName: request.user.lastName,
            phone: request.user.phone,
            chatbotTokenLimitMonthly:
                request.user.chatbotTokenLimitMonthly ?? 0,
            chatbotEnabled: request.chatbotEnabled,
        };

        return apiClient
            .put<ProfileFormData, User>(
                `/users/${request.user.id}/admin`,
                formData,
            )
            .then((updatedUser: User) => {
                this.context.commit('setError', null);
                this.context.commit('setUser', updatedUser);

                return updatedUser;
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updatePassword(request: UpdateForm<PasswordFormData>): Promise<void> {
        return apiClient
            .put(`/users/${request.id}/password`, request.formData)
            .then(() => {
                this.context.commit('setPasswordError', null);
            })
            .catch((error) => {
                this.context.commit('setPasswordError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchPackageHistory(projectId: number): Promise<void> {
        this.context.commit('setHistoryLoading', true);

        return apiClient
            .get(`/projects/${projectId}/package-history`)
            .then((data) => {
                this.context.commit('setPackageHistory', {
                    id: projectId,
                    newPackageHistory: data,
                });
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            })
            .finally(() => {
                this.context.commit('setHistoryLoading', false);
            });
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getPasswordError(): ApiError | null {
        return this.passwordError;
    }

    get getUsers(): Pageable<User> | null {
        return this.users;
    }

    get getEvents(): Pageable<EventResponse> | null {
        return this.events;
    }

    get getDocuments(): ManagedDocumentResponse[] {
        return this.documents;
    }

    get getLoading(): boolean {
        return this.loading;
    }

    get getHistoryLoading(): boolean {
        return this.historyLoading;
    }

    get getDocumentsLoading(): boolean {
        return this.documentsLoading;
    }
}
